// ============================================================
// useBattlefieldLibrary - the battlefield's shared map library.
//
// Split out of useBattlefieldSync: the library listener (the document-
// per-map collection, and the pre-v8.310.19 blob it replaced), the
// difference-only writes, and the save / delete / import verbs.
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import * as multiplayer from '../multiplayer';
import { mergeIntoLibrary } from '../engine/battlefield/mapFile';
import { libraryDiff, assembleLibrary } from '../engine/battlefield/mapLibrarySync';
import { diag, errorSummary } from '../diagnostics/log';

/**
 * THE MAP LIBRARY, A DOCUMENT PER MAP (v8.310.19). The collection is the
 * truth; the pre-v8.310.19 blob is read only while the collection is
 * empty, so a session from before keeps its maps until the first write
 * moves them across (see `writeLibraryChanges`). `docIdsRef` holds which
 * documents each map occupies - a map bigger than one document is split
 * into parts, and a rewrite or delete must touch all of them.
 */
function useMapLibrary(sessionId) {
  const [library, setLibraryState] = useState([]);
  const fromBlobRef = useRef(false);
  const docIdsRef = useRef(new Map());
  useEffect(() => {
    if (!sessionId) return undefined;
    let collectionHasMaps = false;
    const offCollection = multiplayer.onMapLibraryUpdate(sessionId, ({ entries }) => {
      const { entries: maps, docIds } = assembleLibrary(entries.map((e) => ({ ...e.data, id: e.id })));
      docIdsRef.current = docIds;
      if (maps.length || collectionHasMaps) setLibraryState(maps);
      if (maps.length) fromBlobRef.current = false;
      collectionHasMaps = maps.length > 0;
    });
    const offBlob = multiplayer.onBattlefieldLibraryUpdate(sessionId, (data, exists) => {
      if (collectionHasMaps || !exists || !Array.isArray(data)) return;
      fromBlobRef.current = true;
      setLibraryState(data);
    });
    return () => {
      offCollection();
      offBlob();
    };
  }, [sessionId]);
  return { library, setLibraryState, fromBlobRef, docIdsRef };
}

/** Write one map; a refusal (an oversized map, with the adapter's sentence) is reported by name. */
function writeMapEntry(sessionId, entry, docIdsRef) {
  return multiplayer
    .writeMapLibraryEntry(sessionId, entry, docIdsRef.current.get(entry.name) ?? [])
    .then((ids) => docIdsRef.current.set(entry.name, ids))
    .catch((err) => {
      diag.error('map-library', `map "${entry.name}" did not save`, errorSummary(err));
      multiplayer.reportWriteFailure(`Map "${entry.name}" could not be shared: ${err?.message ?? err}`, err);
    });
}

function deleteMapEntry(sessionId, name, docIdsRef) {
  return multiplayer.deleteMapLibraryEntry(sessionId, name, docIdsRef.current.get(name) ?? []).catch((err) => {
    diag.error('map-library', `map "${name}" did not delete`, errorSummary(err));
  });
}

/**
 * Only what changed travels: a save writes one map, a delete removes one,
 * and the rest of the library lands even if one map is refused. A library
 * read from the old blob is written out whole on its first change, and
 * the blob deleted after, so the move happens once.
 */
function writeLibraryChanges(sessionId, prev, next, docIdsRef, migrating) {
  const { set, remove } = libraryDiff(prev, next);
  const writes = [
    ...set.map((entry) => writeMapEntry(sessionId, entry, docIdsRef)),
    ...remove.map((name) => deleteMapEntry(sessionId, name, docIdsRef)),
  ];
  if (!migrating) return;
  void Promise.all(writes).then(() =>
    multiplayer.deleteLegacyBattlefieldLibrary(sessionId).catch((err) => {
      diag.warn('map-library', 'the old library blob could not be deleted', errorSummary(err));
    }),
  );
}

/** Replace the library, locally at once and on the server by difference. */
function useWriteLibrary(sessionId, { library, setLibraryState, fromBlobRef, docIdsRef }) {
  const libraryRef = useRef(library);
  libraryRef.current = library;
  return useCallback(
    (next) => {
      const migrating = fromBlobRef.current;
      const prev = migrating ? [] : libraryRef.current;
      fromBlobRef.current = false;
      setLibraryState(next);
      if (sessionId) writeLibraryChanges(sessionId, prev, next, docIdsRef, migrating);
    },
    [sessionId, setLibraryState, fromBlobRef, docIdsRef],
  );
}

/**
 * The library verbs. Saving writes what the GM can SEE, secrets included -
 * a saved ambush that loses its ambush is not the map they built - and
 * overwrites a namesake: "Docks", "Docks (2)", "Docks (3)" is nobody's
 * idea of a library. Importing follows the same rule through
 * `mergeIntoLibrary`: importing "Docks" over "Docks" is how you update a
 * map you have already shared.
 */
function useLibraryVerbs(visibleTerrain, library, writeLibrary) {
  const saveMap = useCallback(
    (name) => {
      // A map copied from the asset library keeps where it came from when saved over (v11.71.0).
      const origin = library.find((e) => e.name === name)?.origin;
      const entry = { name, terrain: { ...visibleTerrain, name }, ...(origin ? { origin } : {}) };
      writeLibrary([...library.filter((e) => e.name !== name), entry]);
    },
    [visibleTerrain, library, writeLibrary],
  );
  const deleteMap = useCallback(
    (name) => writeLibrary(library.filter((e) => e.name !== name)),
    [library, writeLibrary],
  );
  const importMaps = useCallback(
    (maps) => {
      if (!maps?.length) return 0;
      writeLibrary(mergeIntoLibrary(library, maps));
      return maps.length;
    },
    [library, writeLibrary],
  );
  return { saveMap, deleteMap, importMaps };
}

/**
 * A copy from the asset library, in (v11.71.0): it replaces the copy of the
 * SAME asset - found by its origin, so a map renamed in the library is not
 * left twice - and any map of its name.
 */
function useCopyMap(library, writeLibrary) {
  return useCallback(
    // One entry or SEVERAL in one write: `library` is this render's, so two calls in one tick would each write over
    // the other (the bulk copy, 2026-10-20).
    (entryOrEntries) => {
      const entries = [entryOrEntries].flat();
      const same = (e) =>
        entries.some((n) => e.name === n.name || (e.origin && e.origin.assetId === n.origin?.assetId));
      writeLibrary([...library.filter((e) => !same(e)), ...entries]);
    },
    [library, writeLibrary],
  );
}

/**
 * The shared map library and its verbs. `visibleTerrain` is what a save
 * captures - the GM's view, secrets included.
 */
export default function useBattlefieldLibrary(sessionId, visibleTerrain) {
  const state = useMapLibrary(sessionId);
  const writeLibrary = useWriteLibrary(sessionId, state);
  const copyMap = useCopyMap(state.library, writeLibrary);
  return { library: state.library, ...useLibraryVerbs(visibleTerrain, state.library, writeLibrary), copyMap };
}
